/**
 * ForgeStudioModule — the Forge web studio (F1).
 *
 * An in-browser "studio" on EngineX: a Creator-tier-gated project, a scene/instance
 * hierarchy explorer, an add-instance palette, a property inspector, a fidelity-tier
 * control (bounded by entitlement), and a script editor whose play-test flags
 * out-of-scope capabilities at author time — plus rendering the authored instances
 * as primitive meshes. The client RENDERS replicated rows + CALLS reducers; the
 * gate, provenance, fidelity bound, and cap validation are all server-authoritative.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { FORGE_INSTANCE_KINDS, FORGE_VALID_CAPS } from '@shared/types/forge_authoring.js';
import type { ForgeDiagnostic } from '@shared/types/forge_compile.js';
import { ASSET_BRIDGE_DIRECTION } from '@shared/types/forge_bridge.js';
import { iconMarkup } from '../lib/icons.js';

export class ForgeStudioModule implements IModule {
  readonly name = 'ForgeStudio';
  private scene!: THREE.Scene;
  private bound = false;
  private els: Record<string, HTMLElement> = {};
  private root = new THREE.Group();
  private meshes = new Map<string, THREE.Mesh>();
  private mat = new THREE.MeshStandardMaterial({ color: 0xc8a0ff, roughness: 0.5 });
  private selected: bigint | null = null;

  init(app: App): void {
    this.scene = app.world.scene;
    this.scene.add(this.root);
    for (const id of ['fg-gate', 'fg-tree', 'fg-palette', 'fg-inspector', 'fg-script', 'fg-fidelity', 'fg-compile', 'fg-bridge', 'fg-publish', 'fg-note']) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }
    bus.on('atlas:account-ready', () => {
      this.bind();
      this.render();
    });
    for (const ev of ['atlas:forge-updated', 'atlas:world-entered', 'atlas:exchange-updated'] as const) {
      bus.on(ev, () => this.render());
    }
    this.render();
  }

  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    const ping = (): void => bus.emit('atlas:forge-updated', {});
    for (const tbl of [conn.db.forgeProject, conn.db.forgeInstance, conn.db.forgeScript, conn.db.forgeCompileJob, conn.db.forgeValidation, conn.db.forgeBuild, conn.db.asset, conn.db.inventoryItem, conn.db.assetBridge, conn.db.forgeRelease, conn.db.publishedExperience, conn.db.creatorCert]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping);
      t.onUpdate(ping);
      t.onDelete(ping);
    }
  }

  /** The Forge project for the current world the player owns, if any. */
  private project(conn: DbConnection, me: Identity) {
    const wid = atlas.currentWorldId;
    if (wid === null) return null;
    return [...conn.db.forgeProject.iter()].find((p) => p.worldId === wid && p.owner.isEqual(me)) ?? null;
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return;
    const me = conn.identity;
    const project = this.project(conn, me);
    this.renderGate(conn, me, project);
    this.renderTree(conn, project);
    this.renderPalette(project);
    this.renderInspector(conn);
    this.renderFidelity(conn, me, project);
    this.renderScript(conn);
    this.renderCompile(conn, project);
    this.renderBridge(conn, me, project);
    this.renderPublish(conn, me, project);
    this.renderMeshes(conn, project);
  }

  private renderGate(conn: DbConnection, me: Identity, project: ReturnType<ForgeStudioModule['project']>): void {
    const el = this.els['fg-gate'];
    if (!el) return;
    const ent = conn.db.entitlement.identity.find(me);
    const forge = ent?.forgeAccess === true;
    if (project) {
      el.innerHTML = `<div class="meta">${iconMarkup('build')}Forge project #${project.projectId} — <b>${this.esc(project.status)}</b> · SDK ${this.esc(project.sdkVersion)}</div>`;
      return;
    }
    const wid = atlas.currentWorldId;
    const world = wid !== null ? conn.db.world.worldId.find(wid) : undefined;
    const ownHere = world ? world.owner.isEqual(me) : false;
    if (!forge) {
      el.innerHTML = '<div class="empty">Forge authoring needs a Creator-tier subscription (forge_access). Upgrade in the Exchange tab.</div>';
    } else if (!ownHere) {
      el.innerHTML = '<div class="empty">Open a world you own (your Personal or an Individual world) to forge it.</div>';
    } else {
      el.innerHTML = '<div class="meta">Creator tier ✓ — you can forge this world.</div><button class="action" id="fg-create-btn">Create Forge project</button>';
      el.querySelector('#fg-create-btn')?.addEventListener('click', () => this.create(wid!));
    }
  }

  private renderTree(conn: DbConnection, project: ReturnType<ForgeStudioModule['project']>): void {
    const el = this.els['fg-tree'];
    if (!el) return;
    if (!project) {
      el.innerHTML = '';
      return;
    }
    const instances = [...conn.db.forgeInstance.iter()].filter((i) => i.projectId === project.projectId);
    const byParent = new Map<string, typeof instances>();
    for (const i of instances) {
      const k = i.parentId.toString();
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k)!.push(i);
    }
    const lines: string[] = [];
    const walk = (parentId: bigint, depth: number): void => {
      for (const i of byParent.get(parentId.toString()) ?? []) {
        const sel = this.selected === i.instanceId ? ' style="background:var(--ax-bg3)"' : '';
        lines.push(
          `<div class="meta" data-sel="${i.instanceId.toString()}"${sel}>${'&nbsp;'.repeat(depth * 3)}${depth > 0 ? '└ ' : ''}${this.esc(i.kind)} #${i.instanceId}${i.assetId !== 0n ? ` ${iconMarkup('box')}` : ''}</div>`,
        );
        walk(i.instanceId, depth + 1);
      }
    };
    walk(0n, 0); // scene root has parentId 0
    el.innerHTML = lines.join('') || '<div class="empty">No instances.</div>';
    for (const row of Array.from(el.querySelectorAll('[data-sel]'))) {
      row.addEventListener('click', () => {
        this.selected = BigInt((row as HTMLElement).dataset.sel!);
        this.render();
      });
    }
  }

  private renderPalette(project: ReturnType<ForgeStudioModule['project']>): void {
    const el = this.els['fg-palette'];
    if (!el) return;
    if (!project) {
      el.innerHTML = '';
      return;
    }
    const where = this.selected !== null ? `under #${this.selected}` : 'under the scene root';
    el.innerHTML =
      `<div class="meta">Add an instance ${where}:</div>` +
      FORGE_INSTANCE_KINDS.map((k) => `<button class="action secondary" data-add="${k}">${k}</button>`).join(' ');
    for (const btn of Array.from(el.querySelectorAll('[data-add]'))) {
      btn.addEventListener('click', () => this.add(project.projectId, (btn as HTMLElement).dataset.add!));
    }
  }

  private renderInspector(conn: DbConnection): void {
    const el = this.els['fg-inspector'];
    if (!el) return;
    if (this.selected === null) {
      el.innerHTML = '<div class="empty">Select an instance to inspect.</div>';
      return;
    }
    const inst = conn.db.forgeInstance.instanceId.find(this.selected);
    if (!inst) {
      el.innerHTML = '';
      return;
    }
    el.innerHTML =
      `<div class="meta">${this.esc(inst.kind)} #${inst.instanceId}${inst.assetId !== 0n ? ` · asset #${inst.assetId}` : ''}</div>` +
      `<div class="row"><label>transform</label><input id="fg-tf" value='${this.esc(inst.transform)}' style="width:100%" /></div>` +
      `<div class="row"><label>props</label><input id="fg-pr" value='${this.esc(inst.props)}' style="width:100%" /></div>` +
      `<button class="action secondary" id="fg-upd-btn">Apply</button>`;
    el.querySelector('#fg-upd-btn')?.addEventListener('click', () => {
      const tf = (el.querySelector('#fg-tf') as HTMLInputElement).value;
      const pr = (el.querySelector('#fg-pr') as HTMLInputElement).value;
      this.update(inst.instanceId, tf, pr);
    });
  }

  private renderFidelity(conn: DbConnection, me: Identity, project: ReturnType<ForgeStudioModule['project']>): void {
    const el = this.els['fg-fidelity'];
    if (!el) return;
    if (!project) {
      el.innerHTML = '';
      return;
    }
    const tier = conn.db.entitlement.identity.find(me)?.tier ?? 'free';
    const cap = tier === 'tier_sovereign' ? 4 : tier === 'tier_creator' ? 3 : 1;
    el.innerHTML =
      `<div class="meta">${iconMarkup('gear')}Fidelity tier ${project.fidelityTier} / cap ${cap} (${this.esc(tier)})</div>` +
      [1, 2, 3, 4].map((t) => `<button class="action secondary" data-fid="${t}"${t > cap ? ' disabled' : ''}>${t}</button>`).join(' ');
    for (const btn of Array.from(el.querySelectorAll('[data-fid]'))) {
      btn.addEventListener('click', () => this.setFidelity(project.projectId, Number((btn as HTMLElement).dataset.fid)));
    }
  }

  private renderScript(conn: DbConnection): void {
    const el = this.els['fg-script'];
    if (!el) return;
    if (this.selected === null) {
      el.innerHTML = '';
      return;
    }
    const sid = this.selected;
    const script = [...conn.db.forgeScript.iter()].find((s) => s.instanceId === sid);
    const caps = FORGE_VALID_CAPS.map(
      (c) => `<label style="margin-right:8px"><input type="checkbox" class="fg-cap" value="${c}"${script && script.capsRequested.includes(c) ? ' checked' : ''}/> ${c}</label>`,
    ).join('');
    const flag = script ? (script.capsValid ? '<span class="price">✓ caps valid</span>' : '<span class="price" style="color:var(--ax-bad)">⚠ out-of-scope cap</span>') : '';
    el.innerHTML =
      `<div class="meta">Script on #${sid} ${flag}</div>` +
      `<textarea id="fg-src" rows="4" style="width:100%" placeholder="-- Forge SDK Lua (compiled in F2)">${script ? this.esc(script.source) : ''}</textarea>` +
      `<div class="meta">Requested capabilities (P5):</div><div>${caps}</div>` +
      `<button class="action" id="fg-play-btn">Save &amp; play-test</button>`;
    el.querySelector('#fg-play-btn')?.addEventListener('click', () => {
      const src = (el.querySelector('#fg-src') as HTMLTextAreaElement).value;
      const chosen = Array.from(el.querySelectorAll('.fg-cap'))
        .filter((c) => (c as HTMLInputElement).checked)
        .map((c) => (c as HTMLInputElement).value);
      this.setScript(sid, src, chosen);
    });
  }

  /** The F2 compile & sign panel: compile button, job lifecycle, signed build, diagnostics. */
  private renderCompile(conn: DbConnection, project: ReturnType<ForgeStudioModule['project']>): void {
    const el = this.els['fg-compile'];
    if (!el) return;
    if (!project) {
      el.innerHTML = '';
      return;
    }
    const pid = project.projectId;
    const job = [...conn.db.forgeCompileJob.iter()].filter((j) => j.projectId === pid).sort((a, b) => (a.jobId < b.jobId ? 1 : -1))[0];
    const build = [...conn.db.forgeBuild.iter()].filter((b) => b.projectId === pid).sort((a, b) => (a.buildId < b.buildId ? 1 : -1))[0];

    const parts: string[] = ['<button class="action" id="fg-compile-btn">Compile &amp; sign</button>'];
    if (job) {
      const inFlight = job.status === 'queued' || job.status === 'compiling';
      const badge =
        job.status === 'signed'
          ? '<span class="price">✓ signed</span>'
          : job.status === 'failed'
            ? '<span class="price" style="color:var(--ax-bad)">✗ failed (unsigned)</span>'
            : `<span class="price">… ${this.esc(job.status)}</span>`;
      parts.push(`<div class="meta">Latest job #${job.jobId} — ${badge}</div>`);
      if (inFlight) parts.push('<button class="action secondary" id="fg-worker-btn">▶ Run worker (dev)</button>');
      if (job.status === 'failed' && job.diagnostics) {
        try {
          const diags = JSON.parse(job.diagnostics) as ForgeDiagnostic[];
          parts.push('<div class="meta">Diagnostics:</div>');
          for (const d of diags) parts.push(`<div class="meta" style="color:var(--ax-bad)">• [${this.esc(d.kind)}] ${this.esc(d.scope)} — ${this.esc(d.detail)}</div>`);
        } catch {
          /* malformed — skip */
        }
      }
    }
    if (build) {
      const v = build.verified
        ? '<span class="price">✓ verified</span>'
        : '<span class="price" style="color:var(--ax-bad)">⚠ unverified/tampered</span>';
      parts.push(`<div class="meta">Signed build #${build.buildId} · hash <code>${this.esc(build.bytecodeHash)}</code> ${v}</div>`);
      parts.push(`<div class="meta">sig <code>${this.esc(build.sig.slice(0, 16))}…</code></div>`);
      parts.push('<button class="action secondary" id="fg-verify-btn">Verify integrity</button>');
    }
    el.innerHTML = parts.join('');
    el.querySelector('#fg-compile-btn')?.addEventListener('click', () => this.compile(pid));
    el.querySelector('#fg-worker-btn')?.addEventListener('click', () => {
      if (job) this.runWorker(job.jobId);
    });
    el.querySelector('#fg-verify-btn')?.addEventListener('click', () => this.verifyBuild(pid));
  }

  /** The F3 bridge & lineage panel: export owned assets, import (remix) Forge assets, crossings. */
  private renderBridge(conn: DbConnection, me: Identity, project: ReturnType<ForgeStudioModule['project']>): void {
    const el = this.els['fg-bridge'];
    if (!el) return;
    if (!project) {
      el.innerHTML = '';
      return;
    }
    const pid = project.projectId;
    const parts: string[] = [];

    // My owned assets → export (reference) into this Forge project.
    const ownedIds = new Set<string>();
    for (const it of conn.db.inventoryItem.iter()) if (it.owner.isEqual(me)) ownedIds.add(it.assetId.toString());
    parts.push('<div class="meta">Export an owned Canvas asset → Forge:</div>');
    if (ownedIds.size === 0) parts.push('<div class="empty">No owned assets yet (create one in the Market/Inventory tab).</div>');
    for (const s of [...ownedIds].slice(0, 8)) {
      const a = conn.db.asset.assetId.find(BigInt(s));
      if (!a) continue;
      parts.push(`<div class="meta">#${a.assetId} ${this.esc(a.kind)}${a.isPremium ? ` ${iconMarkup('star')}premium` : ''}${a.permRemix ? '' : ` ${iconMarkup('lock')}no-remix`} <button class="action secondary" data-export="${a.assetId.toString()}">Export</button></div>`);
    }

    // Assets referenced in this project → import (remix) to Canvas.
    const refIds = new Set<string>();
    for (const i of conn.db.forgeInstance.iter()) if (i.projectId === pid && i.assetId !== 0n) refIds.add(i.assetId.toString());
    if (refIds.size > 0) {
      parts.push('<div class="meta">Forge assets here → import (remix) to Canvas:</div>');
      for (const s of [...refIds].slice(0, 8)) {
        const a = conn.db.asset.assetId.find(BigInt(s));
        if (!a) continue;
        parts.push(`<div class="meta">#${a.assetId} ${this.esc(a.kind)} <button class="action secondary" data-import="${a.assetId.toString()}">Import (remix)</button></div>`);
      }
    }

    // Recent crossings (lineage trace).
    const crossings = [...conn.db.assetBridge.iter()].sort((x, y) => (x.bridgeId < y.bridgeId ? 1 : -1)).slice(0, 6);
    if (crossings.length > 0) {
      parts.push('<div class="meta">Recent crossings (lineage):</div>');
      for (const b of crossings) {
        const arrow = b.direction === ASSET_BRIDGE_DIRECTION.CanvasToForge ? 'Canvas → Forge ↑' : 'Forge → Canvas ↓';
        const note = b.resultAssetId !== b.assetId ? ` → derivative #${b.resultAssetId}` : ' (same asset)';
        parts.push(`<div class="meta">• #${b.assetId} ${arrow} @tier ${b.fidelityTier}${note}</div>`);
      }
    }

    el.innerHTML = parts.join('');
    for (const btn of Array.from(el.querySelectorAll('[data-export]'))) {
      btn.addEventListener('click', () => this.exportAsset(BigInt((btn as HTMLElement).dataset.export!), pid));
    }
    for (const btn of Array.from(el.querySelectorAll('[data-import]'))) {
      btn.addEventListener('click', () => this.importAsset(BigInt((btn as HTMLElement).dataset.import!)));
    }
  }

  /** The F4 publish panel: publish/version history, rollback, monetization, verified load. */
  private renderPublish(conn: DbConnection, me: Identity, project: ReturnType<ForgeStudioModule['project']>): void {
    const el = this.els['fg-publish'];
    if (!el) return;
    if (!project) {
      el.innerHTML = '';
      return;
    }
    const pid = project.projectId;
    const wid = project.worldId;
    const hasSignedBuild = [...conn.db.forgeBuild.iter()].some((b) => b.projectId === pid && b.signed);
    const pe = conn.db.publishedExperience.worldId.find(wid);
    const liveId = pe?.liveReleaseId ?? 0n;
    const certified = conn.db.creatorCert.identity.find(me)?.certified === true;
    const releases = [...conn.db.forgeRelease.iter()].filter((r) => r.projectId === pid).sort((a, b) => b.version - a.version);

    const parts: string[] = [];
    parts.push(
      `<button class="action" id="fg-publish-btn"${hasSignedBuild ? '' : ' disabled'}>Publish ${hasSignedBuild ? 'signed build' : '(compile first)'}</button>`,
    );
    if (pe) parts.push(`<button class="action secondary" id="fg-load-btn">Load (verify) — ${pe.runtimeVerified ? '✓ verified' : 'unverified'}</button>`);

    if (releases.length > 0) {
      parts.push('<div class="meta">Version history:</div>');
      for (const r of releases.slice(0, 8)) {
        const live = r.releaseId === liveId ? ' <span class="price">● live</span>' : '';
        const money = r.monetized ? ` ${iconMarkup('diamond')}${r.priceSov} SOV` : ' (free)';
        const rollback = r.releaseId !== liveId ? ` <button class="action secondary" data-rollback="${r.releaseId.toString()}">Roll back</button>` : '';
        parts.push(`<div class="meta">v${r.version} #${r.releaseId}${money}${live}${rollback}</div>`);
      }
      // Monetization on the live release.
      const liveRel = releases.find((r) => r.releaseId === liveId);
      if (liveRel) {
        if (certified) {
          parts.push(
            `<div class="meta">Monetize v${liveRel.version}: <input id="fg-price" type="number" min="1" value="${liveRel.priceSov > 0n ? liveRel.priceSov.toString() : '500'}" style="width:120px" /> SOV <button class="action secondary" id="fg-monetize-btn">Set price</button></div>`,
          );
        } else {
          parts.push('<div class="meta">Monetization needs Creator Certification (request it in the Exchange tab). Publishing is free without it.</div>');
        }
      }
    }

    el.innerHTML = parts.join('');
    el.querySelector('#fg-publish-btn')?.addEventListener('click', () => this.publish(pid));
    el.querySelector('#fg-load-btn')?.addEventListener('click', () => this.loadExperience(wid));
    el.querySelector('#fg-monetize-btn')?.addEventListener('click', () => {
      const price = (el.querySelector('#fg-price') as HTMLInputElement).value;
      if (liveId !== 0n) this.monetize(liveId, price);
    });
    for (const btn of Array.from(el.querySelectorAll('[data-rollback]'))) {
      btn.addEventListener('click', () => this.rollback(wid, BigInt((btn as HTMLElement).dataset.rollback!)));
    }
  }

  /** Render each authored instance as a primitive mesh at its transform. */
  private renderMeshes(conn: DbConnection, project: ReturnType<ForgeStudioModule['project']>): void {
    const live = new Set<string>();
    if (project) {
      for (const i of conn.db.forgeInstance.iter()) {
        if (i.projectId !== project.projectId || i.kind === 'folder' || i.kind === 'script') continue;
        const key = i.instanceId.toString();
        live.add(key);
        let mesh = this.meshes.get(key);
        if (!mesh) {
          mesh = new THREE.Mesh(this.geoFor(i.kind), this.mat);
          this.root.add(mesh);
          this.meshes.set(key, mesh);
        }
        let pos = [0, 1, 0];
        try {
          const tf = JSON.parse(i.transform) as { pos?: number[] };
          if (Array.isArray(tf.pos) && tf.pos.length === 3) pos = tf.pos;
        } catch {
          /* keep default */
        }
        mesh.position.set(pos[0], pos[1], pos[2]);
      }
    }
    for (const [key, mesh] of this.meshes) {
      if (!live.has(key)) {
        this.root.remove(mesh);
        mesh.geometry.dispose();
        this.meshes.delete(key);
      }
    }
  }

  private geoFor(kind: string): THREE.BufferGeometry {
    switch (kind) {
      case 'light':
        return new THREE.SphereGeometry(0.3, 12, 8);
      case 'spawn':
        return new THREE.ConeGeometry(0.4, 1, 8);
      case 'trigger':
        return new THREE.BoxGeometry(1.2, 0.1, 1.2);
      default:
        return new THREE.BoxGeometry(1, 1, 1);
    }
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  private create(worldId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .createForgeProject({ worldId, sdkVersion: '1.0.0' })
      .then(() => this.note('Forge project created.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private add(projectId: bigint, kind: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .addForgeInstance({ projectId, parentId: this.selected ?? 0n, kind, transform: '{"pos":[0,1,0]}', props: '{}', assetId: 0n })
      .then(() => this.note(`Added a ${kind}.`, 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private update(instanceId: bigint, transform: string, props: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers.updateForgeInstance({ instanceId, transform, props }).then(() => this.note('Instance updated.', 'ok')).catch((e: Error) => this.note(e.message, 'err'));
  }

  private setFidelity(projectId: bigint, tier: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers.setFidelityTier({ projectId, tier }).then(() => this.note(`Fidelity → ${tier} (bounded by your tier).`, 'ok')).catch((e: Error) => this.note(e.message, 'err'));
  }

  private setScript(instanceId: bigint, source: string, caps: string[]): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .setForgeScript({ instanceId, source, capsRequested: JSON.stringify(caps) })
      .then(() => this.note('Script saved + play-tested (caps validated).', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private compile(projectId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .compileForge({ projectId })
      .then(() => this.note('Compile job queued (async, off the hot path).', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  /** Drive the externally-driven worker (a real deployment polls + calls this). */
  private runWorker(jobId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .runCompileJob({ jobId })
      .then(() => this.note('Worker compiled → validated → signed (or rejected with diagnostics).', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private verifyBuild(projectId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .getCompileResult({ projectId })
      .then(() => this.note('Re-verified the build signature + artifact checksums.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private exportAsset(assetId: bigint, projectId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .exportToForge({ assetId, projectId })
      .then(() => this.note('Exported into Forge — same asset, provenance preserved.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private importAsset(forgeAssetId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .importToCanvas({ forgeAssetId })
      .then(() => this.note('Imported to Canvas (downgraded; perms re-checked, royalty hooked).', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private publish(projectId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .publishForge({ projectId })
      .then(() => this.note('Published an immutable versioned release (world is live on it).', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private rollback(worldId: bigint, releaseId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .rollbackForge({ worldId, releaseId })
      .then(() => this.note('Rolled back — the world re-points to the prior release (no recompile).', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private monetize(releaseId: bigint, price: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    let priceSov: bigint;
    try {
      priceSov = BigInt(price);
    } catch {
      this.note('Enter a whole-number SOV price.', 'err');
      return;
    }
    void conn.reducers
      .setForgeMonetization({ releaseId, priceSov })
      .then(() => this.note('Monetized — settles 85/15 through the Creator Exchange.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private loadExperience(worldId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .loadPublishedExperience({ worldId })
      .then(() => this.note('Loaded — the signed build was verified before running.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private note(text: string, cls: '' | 'ok' | 'err'): void {
    const el = this.els['fg-note'];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'));
  }

  dispose(): void {
    for (const m of this.meshes.values()) m.geometry.dispose();
    this.mat.dispose();
  }
}
